import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchReviews } from './api';
import { ApiConfig, ReviewPage, SortOption, Tab, View } from './types';

const SEARCH_DEBOUNCE_MS = 400;

type Options = {
  api: ApiConfig;
  sortOptions: SortOption[];
  /** Page one of the requested tab, already rendered by the server. */
  initial: ReviewPage;
  /** Where the tab links point, so a toggle writes the same url the server would. */
  tabUrls: Record<Tab, string>;
};

function signature(view: View): string {
  return [view.tab, view.page, view.sort, view.ratings.join('-'), view.search].join('|');
}

/**
 * The whole page's state, and the only thing that talks to SmartCustomer.
 *
 * Every visitor action here is an API call rather than a navigation, which is the point
 * of the rebuild: switching tabs, turning a page, sorting, filtering and searching all
 * update in place. Two consequences the rest of the components rely on:
 *
 * - **Only the tab reaches the url.** A link to the product reviews is worth sending
 *   someone; a link to page 7 of a four-star filter is not, and thousands of those
 *   spawned off a page that ranks top 3 is a real cost. So the tab is pushed into
 *   history and everything else lives only here.
 * - **Page one of the current tab is already loaded.** The server rendered it, so it is
 *   seeded into the cache rather than fetched again — a visitor who lands, wanders and
 *   comes back does not wait for something already on the page.
 */
export default function useReviews({ api, sortOptions, initial, tabUrls }: Options) {
  const defaultSort = useMemo(
    () => sortOptions.find((option) => option.default) ?? sortOptions[0],
    [sortOptions]
  );

  const initialView: View = useMemo(
    () => ({ tab: initial.tab, page: 1, sort: defaultSort.value, ratings: [], search: '' }),
    [initial.tab, defaultSort.value]
  );

  const [view, setView] = useState<View>(initialView);
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState<ReviewPage | null>(initial.reviews.length > 0 ? initial : null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);

  const tabRef = useRef(view.tab);
  tabRef.current = view.tab;

  // Seeded with what the server already drew, so returning to it costs nothing. Bounded
  // by what one visitor actually clicks, which is why it does not need evicting.
  const cache = useRef<Record<string, ReviewPage>>(
    initial.reviews.length > 0 ? { [signature(initialView)]: initial } : {}
  );

  const sort = useMemo(
    () => sortOptions.find((option) => option.value === view.sort) ?? defaultSort,
    [sortOptions, view.sort, defaultSort]
  );

  useEffect(() => {
    const key = signature(view);
    const cached = cache.current[key];

    if (cached) {
      setPage(cached);
      setIsLoading(false);
      setHasFailed(false);

      return undefined;
    }

    const controller = new AbortController();

    setIsLoading(true);
    setHasFailed(false);

    fetchReviews(api, view, sort, controller.signal)
      .then((result) => {
        cache.current[key] = result;
        setPage(result);
        setIsLoading(false);
      })
      .catch((error: unknown) => {
        // An abort is this effect being superseded, not a failure — the newer request
        // owns the state now and saying "we could not load these" over it would be a lie.
        if (controller.signal.aborted) {
          return;
        }

        console.error('Failed to load reviews', error);
        setHasFailed(true);
        setIsLoading(false);
      });

    return () => controller.abort();
    // `sort` is derived from view.sort, and `api` is a prop that never changes identity
    // across a render of this page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature(view)]);

  // The search box drives its own input and only becomes a request once the visitor
  // stops typing — one call per search rather than one per keystroke.
  useEffect(() => {
    if (searchInput === view.search) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setView((current) => ({ ...current, search: searchInput, page: 1 }));
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // One definition of what changing tab means, so that arriving there by clicking and
  // arriving there with the Back button cannot leave the page in different states.
  // Filters are per tab in every sense a visitor cares about: a magazine title search
  // means nothing against reviews of the company.
  const selectTab = useCallback((tab: Tab) => {
    setSearchInput('');
    setView((current) =>
      current.tab === tab ? current : { tab, page: 1, sort: current.sort, ratings: [], search: '' }
    );
  }, []);

  // Back and forward move between the tabs, because the tabs are the only thing that
  // ever went into history.
  useEffect(() => {
    const onPopState = () => {
      selectTab(new URLSearchParams(window.location.search).get('tab') === 'products' ? 'products' : 'seller');
    };

    window.addEventListener('popstate', onPopState);

    return () => window.removeEventListener('popstate', onPopState);
  }, [selectTab]);

  const goToTab = useCallback(
    (tab: Tab) => {
      // Clicking the tab already showing is not a navigation. Pushing anyway would stack
      // identical entries a visitor then has to press Back through to leave the page.
      if (tab === tabRef.current) {
        return;
      }

      selectTab(tab);
      window.history.pushState({}, '', tabUrls[tab]);
    },
    [selectTab, tabUrls]
  );

  const goToPage = useCallback((next: number) => {
    setView((current) => (current.page === next ? current : { ...current, page: next }));
  }, []);

  const changeSort = useCallback((value: string) => {
    // Page 7 of "most recent" and page 7 of "highest rated" have nothing to do with each
    // other, so a new sort starts again at the beginning.
    setView((current) => ({ ...current, sort: value, page: 1 }));
  }, []);

  const toggleRating = useCallback((stars: number) => {
    setView((current) => {
      const ratings = current.ratings.includes(stars)
        ? current.ratings.filter((rating) => rating !== stars)
        : [...current.ratings, stars].sort();

      return { ...current, ratings, page: 1 };
    });
  }, []);

  const clearRatings = useCallback(() => {
    setView((current) => (current.ratings.length === 0 ? current : { ...current, ratings: [], page: 1 }));
  }, []);

  const clearFilters = useCallback(() => {
    setSearchInput('');
    setView((current) => ({ ...current, ratings: [], search: '', page: 1 }));
  }, []);

  const totalPages = page ? Math.max(1, Math.ceil(page.total / api.perPage)) : 1;

  return {
    view,
    sort,
    page,
    isLoading,
    hasFailed,
    totalPages,
    searchInput,
    setSearchInput,
    goToTab,
    goToPage,
    changeSort,
    toggleRating,
    clearRatings,
    clearFilters,
    isFiltered: view.ratings.length > 0 || view.search !== '',
  };
}
